import { ApiError } from "../../lib/api";
import { t, type TranslationKey } from "../locale";

/**
 * Visitor-facing text for a failed register / login / contact / data-input
 * submit.
 *
 * Audit 2026-09-23 (4f): the Worker's `{error:"Invalid credentials"}` (401),
 * the lockout (429), a Mongo outage (503) and `errorCode:"duplicateGuest"`
 * (409) all reached the guest as raw English. Only the MACHINE-READABLE half
 * is mapped here — an `errorCode` or a status.
 *
 * Fix round 1 (l9 #1) + round 2 (r2 2a/2b): a closed door answers with the
 * operator's own sentence (`loginBlockMessage`, resolved into the guest's
 * language) — unless none is configured, in which case the Worker falls back
 * to ITS OWN English constant and tags the body `defaultMessage: true`
 * (`website-builder-backend/src/lib/login-block.ts` `blockedBody`, eight
 * doors, two of them WITHOUT an errorCode). The flag alone decides: a tagged
 * body is translated into the caller's `closedKey`; an operator sentence
 * (never tagged) passes through verbatim, on every status the doors use (403
 * register/login, 401 data-input submit). Keying on the flag rather than on
 * the constant or the code keeps the SPA independent of the Worker's wording.
 */
const CODE_KEYS: Readonly<Record<string, TranslationKey>> = {
  duplicateGuest: "errorDuplicateGuest",
  additionalFieldsValidationFailed: "submitValidationFailed",
  // W1e: core fields with an object/array/function value are refused.
  invalidFieldType: "errorInvalidFieldType",
};

/**
 * Codes the closed doors answer with. Untagged, their body is operator copy,
 * which must not fall into the status mapping below — the 401 data-input
 * submit door (runtime.ts:4240) would otherwise read "invalid credentials".
 */
const CLOSED_DOOR_CODES: ReadonlySet<string> = new Set([
  "outsideSchedule",
  "registrationClosed",
]);

const STATUS_KEYS: Readonly<Record<number, TranslationKey>> = {
  429: "errorTooManyAttempts",
  503: "errorServiceUnavailable",
};

function errorCodeOf(data: unknown): string | null {
  if (data && typeof data === "object") {
    const code = (data as { errorCode?: unknown }).errorCode;
    if (typeof code === "string" && code) return code;
  }
  return null;
}

function isDefaultMessage(data: unknown): boolean {
  return (
    !!data &&
    typeof data === "object" &&
    (data as { defaultMessage?: unknown }).defaultMessage === true
  );
}

export type SubmitErrorOptions = {
  /**
   * What a bare 401 means on THIS form. The login door answers a wrong code
   * with 401, hence the default; on a data-input form a 401 is a lost
   * session, so that caller passes `sessionExpired`.
   */
  unauthorizedKey?: TranslationKey;
  /**
   * The closed-door sentence for THIS form. The login form passes
   * `loginClosed` — the same sentence its pre-render blocked card shows — so
   * one state never reads two ways on one surface.
   */
  closedKey?: TranslationKey;
};

export function submitErrorText(
  error: unknown,
  fallback: string,
  {
    unauthorizedKey = "errorInvalidCredentials",
    closedKey = "registrationClosed",
  }: SubmitErrorOptions = {},
): string {
  if (error instanceof ApiError) {
    const code = errorCodeOf(error.data);
    const byCode = code !== null ? CODE_KEYS[code] : undefined;
    if (byCode) return t(byCode);
    if (isDefaultMessage(error.data)) return t(closedKey);
    if (code !== null && CLOSED_DOOR_CODES.has(code)) return error.message;
    if (error.status === 401) return t(unauthorizedKey);
    const byStatus = STATUS_KEYS[error.status];
    if (byStatus) return t(byStatus);
    return error.message;
  }
  return error instanceof Error ? error.message : fallback;
}
